import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient';

interface ReauthModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  orgName: string;
}

export const ReauthModal: React.FC<ReauthModalProps> = ({ isOpen, onClose, orgId, orgName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Generate code verifier for PKCE
  const generateCodeVerifier = () => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 128; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      result += charset[randomIndex];
    }
    return result;
  };

  // Generate code challenge from verifier
  const generateCodeChallenge = async (codeVerifier: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const handleReauth = async () => {
    setLoading(true);
    setError(null);

    try {
      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      // Store org ID, code verifier, and current URL for callback
      sessionStorage.setItem('reauth_org_id', orgId);
      sessionStorage.setItem('salesforce_code_verifier', codeVerifier);
      sessionStorage.setItem('reauth_return_url', window.location.pathname + window.location.search);
      
      // Get the org details
      const endpoint = '/api/salesforce/refresh-org-auth';
      let data;
      
      if (isEdgeFunctionMigrated(endpoint)) {
        // Use Edge Function
        const functionName = getEdgeFunctionName(endpoint);
        data = await callEdgeFunction(functionName!, { orgId });
      } else {
        // Fallback to Vercel API
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orgId })
        });

        if (!response.ok) {
          throw new Error('Failed to get org details');
        }

        data = await response.json();
      }

      if (data.needs_auth) {
        // Build OAuth URL with PKCE
        const authUrl = data.is_sandbox
          ? 'https://test.salesforce.com/services/oauth2/authorize'
          : 'https://login.salesforce.com/services/oauth2/authorize';

        // Use environment variable - NO DYNAMIC URL GENERATION
        const redirectUri = import.meta.env.VITE_SALESFORCE_CALLBACK_URL;
        
        // Create state with reauth info
        const stateData = {
          isReauth: true,
          orgId: orgId,
          returnUrl: window.location.pathname + window.location.search
        };
        const state = btoa(JSON.stringify(stateData));
        
        const oauthUrl = `${authUrl}?` + new URLSearchParams({
          response_type: 'code',
          client_id: data.consumer_key || '',
          redirect_uri: redirectUri,
          scope: 'api refresh_token offline_access',
          state: state,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
          prompt: 'login consent'
        });
        
        // Redirect to Salesforce OAuth
        window.location.href = oauthUrl;
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      console.error('Re-authentication error:', err);
      setError(err instanceof Error ? err.message : 'Failed to re-authenticate');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900">Authentication Required</h3>
          <p className="mt-2 text-sm text-gray-600">
            Your Salesforce session for <strong>{orgName}</strong> has expired. 
            Please re-authenticate to continue.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
          <button
            type="button"
            onClick={handleReauth}
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
          >
            {loading ? 'Redirecting...' : 'Re-authenticate'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};